import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { useTranslation } from 'react-i18next';

import { useInspectorStyles } from '../styles';
import { useLocalizationStyles } from './styles';
import { ISpotLocalization } from './types';

interface VideoOverrideCardProps {
  code: string;
  languageName: string;
  override: ISpotLocalization;
  isActive?: boolean;
  isUploadingVideo?: boolean;
  isUploadingPreview?: boolean;
  videoFailureMessage?: string;
  previewFailureMessage?: string;
  onPreviewVideo: (code: string) => void;
  onUploadVideo: (code: string) => void;
  onDeleteVideo: (code: string) => void;
  onPreviewImage: (code: string) => void;
  onUploadPreview: (code: string) => void;
  onDeletePreview: (code: string) => void;
}

const filenameFromUrl = (raw: string | undefined): string => {
  if (!raw) {
    return '';
  }
  try {
    const parsed = new URL(raw, window.location.href);
    return decodeURIComponent(parsed.pathname.split('/').pop() || raw);
  } catch {
    return decodeURIComponent(raw.split('/').pop() || raw);
  }
};

const VideoOverrideCard = ({
  code,
  languageName,
  override,
  isActive = false,
  isUploadingVideo = false,
  isUploadingPreview = false,
  videoFailureMessage,
  previewFailureMessage,
  onPreviewVideo,
  onUploadVideo,
  onDeleteVideo,
  onPreviewImage,
  onUploadPreview,
  onDeletePreview,
}: VideoOverrideCardProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const locStyles = useLocalizationStyles();

  const videoSrc = override.url || override.location;
  const videoFilename = override.filename || filenameFromUrl(videoSrc);
  const previewSrc = override.previewImage;
  const previewFilename =
    override.previewFilename || filenameFromUrl(previewSrc);

  const hasVideo = Boolean(videoFilename || videoSrc);
  const hasPreview = Boolean(previewFilename || previewSrc);

  const canViewVideo = hasVideo && !isUploadingVideo;
  const canViewPreview = hasPreview && !!previewSrc && !isUploadingPreview;

  const savingLabel = translate('editorMode.saving');
  const videoTitle =
    videoFilename ||
    (isUploadingVideo
      ? savingLabel
      : translate('editorMode.localizationNoVideo'));

  return (
    <Box sx={[styles.mediaCard, { marginBottom: '8px' }]}>
      <Box sx={locStyles.videoCardHeader}>
        <Typography
          component="span"
          sx={[
            locStyles.langCode,
            ...(isActive ? [locStyles.langCodeActive] : []),
          ]}
        >
          {code}
        </Typography>
        <Typography
          component="span"
          sx={[locStyles.langName, { flex: 1, width: 'auto' }]}
        >
          {languageName}
        </Typography>
        {isActive && (
          <Typography component="span" sx={locStyles.activeBadge}>
            {translate('editorMode.localizationActiveBadge')}
          </Typography>
        )}
      </Box>

      <Box sx={styles.compactRow}>
        <Tooltip
          title={canViewVideo ? translate('editorMode.viewVideo') : ''}
          placement="bottom"
          enterDelay={400}
        >
          <Box
            sx={[
              styles.compactThumb,
              ...(!hasVideo ? [styles.spotThumbPlaceholder] : []),
              ...(canViewVideo ? [styles.spotThumbClickable] : []),
            ]}
            onClick={canViewVideo ? () => onPreviewVideo(code) : undefined}
            data-testid={`localization-video-${code}-preview-thumb`}
          >
            {hasVideo ? (
              <Box sx={styles.spotThumbVideoBadge}>
                <PlayArrowOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
            ) : (
              <PlayArrowOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
            )}
            {canViewVideo && (
              <Box sx={styles.thumbExpandBadge}>
                <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
              </Box>
            )}
          </Box>
        </Tooltip>

        <Box sx={styles.compactInfo}>
          <Tooltip
            title={videoFilename || ''}
            placement="bottom"
            enterDelay={400}
          >
            <Typography sx={styles.compactTitle}>{videoTitle}</Typography>
          </Tooltip>
          <Typography sx={styles.compactSubtitle}>
            {translate('editorMode.video')}
            {hasVideo ? ' · WebM' : ''}
          </Typography>
          {videoFailureMessage && (
            <Tooltip
              title={videoFailureMessage}
              placement="bottom"
              enterDelay={200}
            >
              <Box sx={styles.previewStatusRow}>
                <ErrorOutlineIcon sx={{ fontSize: 12, color: '#FF6B6B' }} />
                <Typography sx={styles.uploadErrorText}>
                  {translate('editorMode.notUploaded')}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box sx={styles.compactActions}>
          <Tooltip
            title={
              hasVideo
                ? translate('editorMode.replace')
                : translate('editorMode.uploadVideo')
            }
            placement="bottom"
            enterDelay={400}
          >
            <span>
              <IconButton
                size="small"
                sx={styles.iconSquareButton}
                onClick={() => onUploadVideo(code)}
                disabled={isUploadingVideo}
                aria-label={
                  hasVideo
                    ? translate('editorMode.replace')
                    : translate('editorMode.uploadVideo')
                }
                data-testid={`localization-video-${code}-upload-btn`}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={translate('editorMode.localizationRevert')}
            placement="bottom"
            enterDelay={400}
          >
            <span>
              <IconButton
                size="small"
                sx={[styles.iconSquareButton, styles.iconDangerButton]}
                onClick={() => onDeleteVideo(code)}
                disabled={isUploadingVideo}
                aria-label={translate(
                  'editorMode.localizationRevert',
                  'Revert to default',
                )}
                data-testid={`localization-video-${code}-delete-btn`}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {isUploadingVideo && (
        <Box sx={styles.progressBlock}>
          <LinearProgress
            variant="indeterminate"
            sx={styles.compactProgressBar}
          />
          <Box sx={styles.progressTextRow}>
            <CircularProgress size={11} sx={{ color: '#AEB6FF' }} />
            <Typography sx={styles.conversionText}>{savingLabel}</Typography>
          </Box>
        </Box>
      )}

      <Box sx={styles.compactDivider} />

      <Box sx={styles.compactRow}>
        <Tooltip
          title={canViewPreview ? translate('editorMode.viewPreview') : ''}
          placement="bottom"
          enterDelay={400}
        >
          <Box
            sx={[
              styles.compactThumb,
              ...(!hasPreview ? [styles.spotThumbPlaceholder] : []),
              ...(canViewPreview ? [styles.spotThumbClickable] : []),
            ]}
            style={
              hasPreview && previewSrc
                ? { backgroundImage: `url("${previewSrc}")` }
                : undefined
            }
            onClick={canViewPreview ? () => onPreviewImage(code) : undefined}
            data-testid={`localization-video-${code}-preview-image-thumb`}
          >
            {!(hasPreview && previewSrc) && (
              <ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
            )}
            {canViewPreview && (
              <Box sx={styles.thumbExpandBadge}>
                <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
              </Box>
            )}
          </Box>
        </Tooltip>

        <Box sx={styles.compactInfo}>
          <Typography sx={styles.compactTitle}>
            {translate('editorMode.previewImage')}
          </Typography>
          <Box sx={styles.previewStatusRow}>
            {previewFailureMessage ? (
              <Tooltip
                title={previewFailureMessage}
                placement="bottom"
                enterDelay={200}
              >
                <Box sx={styles.previewStatusRow}>
                  <ErrorOutlineIcon sx={{ fontSize: 12, color: '#FF6B6B' }} />
                  <Typography sx={styles.uploadErrorText}>
                    {translate('editorMode.notUploaded')}
                  </Typography>
                </Box>
              </Tooltip>
            ) : (
              <>
                {hasPreview && (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: 12, color: '#4CCBA0' }}
                  />
                )}
                <Tooltip
                  title={hasPreview ? previewFilename : ''}
                  placement="bottom"
                  enterDelay={400}
                >
                  <Typography
                    sx={[
                      styles.compactSubtitle,
                      ...(hasPreview ? [styles.previewStatusOk] : []),
                    ]}
                  >
                    {hasPreview
                      ? previewFilename || translate('editorMode.previewLoaded')
                      : isUploadingPreview
                      ? savingLabel
                      : translate('editorMode.localizationPreviewDefault')}
                  </Typography>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Box sx={styles.compactActions}>
          <Tooltip
            title={
              hasPreview
                ? translate('editorMode.replace')
                : translate('editorMode.uploadPreview')
            }
            placement="bottom"
            enterDelay={400}
          >
            <span>
              <IconButton
                size="small"
                sx={styles.iconSquareButton}
                onClick={() => onUploadPreview(code)}
                disabled={isUploadingPreview}
                aria-label={
                  hasPreview
                    ? translate('editorMode.replace')
                    : translate('editorMode.uploadPreview')
                }
                data-testid={`localization-video-${code}-upload-preview-btn`}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          {hasPreview && (
            <Tooltip
              title={translate('editorMode.localizationUseDefaultPreview')}
              placement="bottom"
              enterDelay={400}
            >
              <span>
                <IconButton
                  size="small"
                  sx={[styles.iconSquareButton, styles.iconDangerButton]}
                  onClick={() => onDeletePreview(code)}
                  disabled={isUploadingPreview}
                  aria-label={translate(
                    'editorMode.localizationUseDefaultPreview',
                  )}
                  data-testid={`localization-video-${code}-delete-preview-btn`}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {isUploadingPreview && (
        <Box sx={styles.progressBlock}>
          <LinearProgress
            variant="indeterminate"
            sx={styles.compactProgressBar}
          />
          <Box sx={styles.progressTextRow}>
            <CircularProgress size={11} sx={{ color: '#AEB6FF' }} />
            <Typography sx={styles.conversionText}>{savingLabel}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VideoOverrideCard;
